import { useState } from "react";
import { ChevronDown, ChevronRight, History, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { tenantLocale, tenantTz } from "../lib/company";

const ACTION_LABEL = {
  edit: "Edited",
  status: "Status",
  yard_link: "Yard link",
  yard_link_attempt: "Link not made",
  storage: "Storage",
};

const fmtAt = (iso) =>
  new Date(iso).toLocaleString(tenantLocale(), {
    timeZone: tenantTz(), day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });

const fmtVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

function HistoryRow({ e }) {
  return (
    <li className="text-xs border-l-2 border-slate-300 dark:border-slate-600 pl-2 py-1" data-testid={`job-history-row-${e.id}`}>
      <div className="flex flex-wrap gap-x-2 text-slate-500 dark:text-slate-400">
        <span className="font-mono">{fmtAt(e.at)}</span>
        <span className="font-semibold text-slate-700 dark:text-slate-200">{ACTION_LABEL[e.action] || e.action}</span>
        <span>by {e.by}</span>
      </div>
      {e.note && <div className="text-slate-700 dark:text-slate-300">{e.note}</div>}
      {Object.entries(e.changes || {}).map(([k, c]) => (
        <div key={k} className="text-slate-600 dark:text-slate-300 break-words">
          <span className="font-mono text-[11px]">{k}</span>: {fmtVal(c.from)} → <b>{fmtVal(c.to)}</b>
        </div>
      ))}
    </li>
  );
}

// Collapsible "who changed what, when" log for a job (staff only).
export default function JobHistoryPanel({ jobId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setRows(null);
      const { data } = await api.get(`/jobs/${jobId}/history`).catch(() => ({ data: [] }));
      setRows(data);
    }
  }

  return (
    <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-3" data-testid="job-history-panel">
      <button type="button" onClick={toggle} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors" data-testid="job-history-toggle">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <History className="h-3.5 w-3.5" /> Change history
      </button>
      {open && (
        rows === null ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400 mt-2" />
        ) : rows.length === 0 ? (
          <div className="text-xs text-slate-400 italic mt-2" data-testid="job-history-empty">No changes recorded yet (history started Oct 2026).</div>
        ) : (
          <ul className="mt-2 space-y-1.5" data-testid="job-history-list">
            {rows.map((e) => <HistoryRow key={e.id} e={e} />)}
          </ul>
        )
      )}
    </div>
  );
}
